import { ref, computed } from 'vue';
import {
  fetchReviewQueue,
  rqMeta,
  rqIsAnomaly,
  rqIsOverdue,
  rqDocName,
  rqOwnerName,
  rqInitials,
  rqSelectedLevel,
  rqSuggestedLevel,
  rqLevelText,
  rqFmtDate,
} from './reviewQueueCore';

/**
 * AttentionQueue (v3 homepage) — completed evaluations awaiting a reviewer's
 * attention, surfacing the recommended level and a BOUNDARY flag (the system
 * recommendation disagrees with the levelled outcome → "verify before
 * approving"). Backed by `POST Report/reviewQueue`; this composable keeps only
 * the items in a review/approval state, ranks boundary cases first, and exposes
 * per-row presentation (recommended level, boundary flag, owner, review CTA).
 * `self` pattern; caller injects an axios-like `http`.
 *
 * @param {Object} options
 * @param {{post:Function}} options.http
 * @param {() => number}  [options.getLimit] rows before "Show all" (default 5)
 * @param {() => ('all'|'mine')} [options.getScope]
 * @param {() => void} [options.onReady] called once after the first fetch
 */
const SERVER_LIMIT = 200;
// States that mean "a reviewer still needs to look at this".
const REVIEW_KEYS = new Set(['breach', 'pending-review', 'pending-approval', 're-review']);

export function attentionQueue(options) {
  const { http, getLimit = () => 5, getScope = () => 'all', onReady = () => {} } = options || {};
  const limit = () => getLimit() || 5;

  const self = { rows: ref([]), loading: ref(false), showAll: ref(false) };

  self.isLoading = computed(() => self.loading.value);

  // Awaiting-review items, boundary (anomaly) cases first, then by recency.
  self.queue = computed(() =>
    self.rows.value
      .filter((r) => REVIEW_KEYS.has(r && r.status_key))
      .slice()
      .sort((a, b) => (rqIsAnomaly(b) ? 1 : 0) - (rqIsAnomaly(a) ? 1 : 0)),
  );
  self.total = computed(() => self.queue.value.length);
  self.boundaryCount = computed(() => self.queue.value.filter(rqIsAnomaly).length);
  self.hasMore = computed(() => self.total.value > limit());
  self.shownRows = computed(() => (self.showAll.value ? self.queue.value : self.queue.value.slice(0, limit())));

  // --- per-row presentation (mirrors the prototype V3Queue row) ---
  self.docName = rqDocName;
  self.ownerName = rqOwnerName;
  self.initials = rqInitials;
  self.isBoundary = rqIsAnomaly;
  self.isOverdue = rqIsOverdue;
  self.meta = rqMeta;
  // "MG-2029 · Evaluated by M. Reyes · 8 Jun 2026"
  self.rowSub = (r) => {
    const who = self.ownerName(r);
    return [r && r.reference, who ? 'Evaluated by ' + who : '', rqFmtDate(r && r.submitted_at)]
      .filter(Boolean)
      .join(' · ');
  };
  // Level cell: boundary shows both levels ("Level 3 / 4 boundary"); otherwise the
  // system recommendation ("Level 7 recommended").
  self.levelText = (r) => {
    const sel = rqSelectedLevel(r);
    const sug = rqSuggestedLevel(r);
    if (self.isBoundary(r) && sel && sug) return `Level ${sel} / ${sug} boundary`;
    const rec = sug || sel;
    return rec ? `Level ${rec} recommended` : '';
  };
  // Trailing dot-pill: boundary → Verify (amber); otherwise For review (neutral).
  self.pillLabel = (r) => (self.isBoundary(r) ? 'Verify' : 'For review');
  self.pillTone = (r) => (self.isBoundary(r) ? 'warning' : 'neutral');

  async function load() {
    self.loading.value = true;
    try {
      self.rows.value = await fetchReviewQueue(http, { scope: getScope() || 'all', limit: SERVER_LIMIT });
    } catch {
      self.rows.value = [];
    } finally {
      self.loading.value = false;
    }
  }

  let didReady = false;
  Promise.resolve(load()).finally(() => {
    if (!didReady) {
      didReady = true;
      onReady();
    }
  });

  return self;
}
